import { fetchUserProfile, invalidateUserProfileMemoryCache } from './uiHandlers';

const MODULE_CACHE_KEY = 'hs_module_access_cache_v2';
const MODULE_CACHE_TTL_MS = 60 * 1000;
let memoryCache = null;

export const getCachedModuleAccess = () => {
  const now = Date.now();
  if (memoryCache && now - memoryCache.ts < MODULE_CACHE_TTL_MS) {
    return memoryCache;
  }
  try {
    const raw = sessionStorage.getItem(MODULE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (now - Number(parsed.ts || 0) >= MODULE_CACHE_TTL_MS) return null;
    memoryCache = parsed;
    return parsed;
  } catch {
    return null;
  }
};

export const getAnyModuleAccessCache = () => {
  if (memoryCache) return memoryCache;
  try {
    const raw = sessionStorage.getItem(MODULE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

export const isModuleAccessCacheStale = (cached) => {
  if (!cached?.ts) return true;
  return Date.now() - Number(cached.ts) >= MODULE_CACHE_TTL_MS;
};

export const clearModuleAccessCache = () => {
  memoryCache = null;
  invalidateUserProfileMemoryCache();
  try {
    sessionStorage.removeItem(MODULE_CACHE_KEY);
  } catch {
    // noop
  }
};

export const setCachedModuleAccess = (payload) => {
  if (!payload || typeof payload !== 'object') return;
  memoryCache = payload;
  try {
    sessionStorage.setItem(MODULE_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // noop
  }
};

const normalizeUser = (user) => {
  if (!user || typeof user !== 'object') return null;
  return {
    ...user,
    module_access: normalizeModuleAccess(user.module_access)
  };
};

const sanitizeUserForModuleCache = (user) => {
  const normalized = normalizeUser(user);
  if (!normalized) return null;
  return {
    must_change_password: normalized.must_change_password === true,
    module_access: normalizeModuleAccess(normalized.module_access)
  };
};

export const buildModuleAccessPayload = (user) => {
  const normalizedUser = sanitizeUserForModuleCache(user);
  return {
    ts: Date.now(),
    user: normalizedUser,
    module_access: normalizeModuleAccess(normalizedUser?.module_access)
  };
};

export const getCachedModuleAccessState = () => {
  const cached = getCachedModuleAccess();
  if (!cached) return null;
  return {
    loading: false,
    module_access: normalizeModuleAccess(cached.module_access),
    user: normalizeUser(cached.user),
    error: null
  };
};

export const getStaleModuleAccessState = () => {
  const cached = getAnyModuleAccessCache();
  if (!cached) return null;
  return {
    loading: false,
    module_access: normalizeModuleAccess(cached.module_access),
    user: normalizeUser(cached.user),
    error: null
  };
};

export const getCachedModuleAccessForRoute = () => {
  const fresh = getCachedModuleAccess();
  if (fresh) return { cached: fresh, stale: false };
  const stale = getAnyModuleAccessCache();
  if (stale) return { cached: stale, stale: true };
  return null;
};

const normalizeModuleKey = (value) => {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
};

/** Claves antiguas / erratas en BD que deben equivaler a MODULE_KEYS.TASK_REPORT_DOWNLOAD */
const canonicalizeModuleKey = (key) => {
  if (key === 'ask_report_download') return 'task_report_download';
  return key;
};

export const normalizeModuleAccess = (rawAccess) => {
  if (rawAccess == null) return [];

  const values = [];
  if (Array.isArray(rawAccess)) {
    rawAccess.forEach((item) => {
      if (item == null) return;
      if (typeof item === 'string' || typeof item === 'number') {
        values.push(item);
        return;
      }
      if (typeof item === 'object') {
        values.push(
          item.key,
          item.module_key,
          item.moduleKey,
          item.code,
          item.name
        );
      }
    });
  } else if (typeof rawAccess === 'string') {
    // Soporta formatos "a,b,c" o "a | b | c"
    rawAccess
      .split(/[,\|;]/)
      .forEach((part) => values.push(part));
  } else if (typeof rawAccess === 'object') {
    values.push(
      rawAccess.key,
      rawAccess.module_key,
      rawAccess.moduleKey,
      rawAccess.code,
      rawAccess.name
    );
  }

  return Array.from(
    new Set(
      values
        .map(normalizeModuleKey)
        .filter(Boolean)
        .map(canonicalizeModuleKey)
    )
  );
};

export const userHasModule = (user, moduleKey) => {
  if (!moduleKey) return true;
  // API antigua sin campo module_access: no restringir (compatibilidad)
  if (user?.module_access === undefined) return true;
  const expected = normalizeModuleKey(moduleKey);
  const access = normalizeModuleAccess(user?.module_access);
  if (access.includes(expected)) return true;
  return false;
};

/** Claves alineadas con ModuleRoute en App.jsx y con la tabla app_modules / Team */
export const MODULE_KEYS = {
  DASHBOARD: 'dashboard',
  SOPORTE: 'soporte',
  JOURNEY_EXPERIENCE: 'journey_experience',
  USER_ADMIN: 'user_admin',
  SERVICE_TICKETS: 'service_tickets',
  DASHBOARD_REPORT: 'dashboard_report',
  TASK_REPORT_DOWNLOAD: 'task_report_download',
  /** Reporte combinado HDS + Zoho (responsable y tiempos por origen) */
  HDS_ZOHO_REPORT: 'hds_zoho_report',
  /** Botón editar solo contrato + servicio en tarjetas del Dashboard */
  TASK_EDIT_CONTRACT_SERVICE: 'task_edit_contract_service',
  MANAGE_SURVEYS: 'manage_surveys',
  AI_CHAT: 'ai_chat',
  /** Módulo migrado desde Prod_Clientes */
  GESTION_CLIENTES: 'gestion_clientes'
};

/**
 * Banderas para mostrar ítems del menú lateral / móvil.
 */
export const getModuleNavFlags = (userLike) => ({
  dashboard: userHasModule(userLike, MODULE_KEYS.DASHBOARD),
  soporte: userHasModule(userLike, MODULE_KEYS.SOPORTE),
  journey_experience: userHasModule(userLike, MODULE_KEYS.JOURNEY_EXPERIENCE),
  user_admin: userHasModule(userLike, MODULE_KEYS.USER_ADMIN),
  service_tickets: userHasModule(userLike, MODULE_KEYS.SERVICE_TICKETS),
  /** Visible solo con el módulo gestion_clientes asignado en Team → Módulos (acceso) */
  gestion_clientes: userHasModule(userLike, MODULE_KEYS.GESTION_CLIENTES)
});

/**
 * Primera ruta permitida según module_access (evita enviar al login al denegar un módulo).
 * Orden: Resumen → Soporte → Reporte Ejecutivo → Journey → Team → Service Tickets.
 */
export const getFirstAllowedHomePathFromModuleAccess = (rawAccess) => {
  if (rawAccess === undefined) return '/overview';
  const access = normalizeModuleAccess(rawAccess);
  if (!access.length) return null;
  const order = [
    [MODULE_KEYS.DASHBOARD, '/overview'],
    [MODULE_KEYS.SOPORTE, '/support'],
    [MODULE_KEYS.DASHBOARD_REPORT, '/report'],
    [MODULE_KEYS.JOURNEY_EXPERIENCE, '/journey-experience'],
    [MODULE_KEYS.USER_ADMIN, '/team'],
    [MODULE_KEYS.SERVICE_TICKETS, '/service-tickets'],
    [MODULE_KEYS.GESTION_CLIENTES, '/gestion-clientes']
  ];
  for (const [key, path] of order) {
    if (access.includes(normalizeModuleKey(key))) return path;
  }
  return null;
};

export const fetchAndCacheModuleAccess = async ({ forceFresh = false } = {}) => {
  const user = await fetchUserProfile({ forceFresh });
  const payload = buildModuleAccessPayload(user);
  setCachedModuleAccess(payload);
  return { user: payload.user, module_access: payload.module_access };
};

